/**
 * Blog listing front-end behaviours — 1:1 port of the legacy
 * `Amri.Blog` jQuery module (`aclsonline/AMRI.WebProject/Scripts/amri.blog.js`):
 *
 *   1. Categories dropdown hover-reveal + click-to-AJAX-load-by-tag
 *   2. "Load More" button fetches the next page + appends to the existing list
 *
 * Server contract (BlogSurfaceController.LoadMore): GET with `page` + `tag`
 * query params, returns JSON `{ items: string, nextPageIndex: number,
 * hasMore: boolean }`.
 */

interface LoadMoreResponse {
    items: string;
    nextPageIndex: number;
    hasMore: boolean;
}

const LOAD_MORE_URL = '/api/blog/load-more';
const PAGE_SIZE = 4;

let currentTag = '';
let currentCategory = '';

function findList(): HTMLElement | null {
    return document.getElementById('post-list-div-to-append');
}

function renderEmpty(): void {
    const wrap = document.getElementById('post-list-div');
    if (wrap) {
        wrap.innerHTML = '<p id="no-posts-message"><i>There are no blog posts to display</i></p>';
    }
}

async function fetchPage(page: number, tag: string, category: string): Promise<LoadMoreResponse | null> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (tag) {
        params.append('tag', tag);
    }
    if (category) {
        params.append('category', category);
    }
    try {
        const response = await fetch(`${LOAD_MORE_URL}?${params}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
            throw new Error(`Load More failed: ${response.status}`);
        }
        return (await response.json()) as LoadMoreResponse;
    } catch (err) {
        console.warn('[blog-list] fetch failed', err);
        return null;
    }
}

function setLoadMoreButton(nextPageIndex: number, hasMore: boolean): void {
    let button = document.querySelector<HTMLAnchorElement>('a.js-load-more-posts');
    if (!hasMore) {
        // Remove the button + its .side-lines wrapper. Legacy markup keeps
        // the wrapper around the button so we drop the wrapper to avoid an
        // empty `<p class="side-lines">` lingering.
        if (button) {
            const wrapper = button.closest('.side-lines');
            (wrapper ?? button).remove();
        }
        return;
    }
    if (button) {
        button.dataset.page = String(nextPageIndex);
        return;
    }
    // Need to ADD a Load More button after a tag-filter that exposed more
    // posts than the initial render shows.
    const postListWrap = document.getElementById('post-list-div');
    if (!postListWrap) {
        return;
    }
    const p = document.createElement('p');
    p.className = 'side-lines';
    const categoryAttr = currentCategory ? ` data-category="${currentCategory}"` : '';
    p.innerHTML = `<span class="side-lines-inner"><a href="#" class="btn-2 js-load-more-posts" data-url="${LOAD_MORE_URL}"${categoryAttr} data-page="${nextPageIndex}">Load More</a></span>`;
    postListWrap.appendChild(p);
}

async function replaceList(tag: string, category: string): Promise<void> {
    currentTag = tag;
    currentCategory = category;
    const data = await fetchPage(0, tag, category);
    if (!data) {
        return;
    }

    let list = findList();
    if (!list) {
        // Empty-state was rendered (no-posts-message); rebuild the .post-list
        // container so the next results have somewhere to land.
        const wrap = document.getElementById('post-list-div');
        if (!wrap) {
            return;
        }
        wrap.innerHTML = '<div class="post-list" id="post-list-div-to-append"></div>';
        list = findList();
    }

    if (!list) {
        return;
    }
    if (!data.items || data.items.trim().length === 0) {
        renderEmpty();
        setLoadMoreButton(0, false);
        return;
    }
    list.innerHTML = data.items;
    setLoadMoreButton(data.nextPageIndex, data.hasMore);
}

function initCategoriesDropdown(): void {
    const dropdown = document.querySelector<HTMLElement>('.post-type-dropdown');
    const header = dropdown?.querySelector<HTMLElement>('.post-type-dropdown-header');
    const menu = dropdown?.querySelector<HTMLElement>('.post-type-menu');
    if (!dropdown || !header || !menu) {
        return;
    }

    // Hover-reveal — mirrors the legacy CSS `:hover` + JS show/hide.
    dropdown.addEventListener('mouseenter', () => { menu.style.display = 'block'; header.classList.add('active-dropdown'); });
    dropdown.addEventListener('mouseleave', () => { menu.style.display = 'none'; header.classList.remove('active-dropdown'); });

    // Click an item → AJAX-load by tag OR category. Per DEV-001 +
    // 2026-06-26 finding: News is category-shaped data in v2 (PR #582
    // doctype collapse), other items remain tag-shaped. Each menu item
    // carries exactly one of data-tag / data-category — pass whichever
    // is present.
    menu.addEventListener('click', async (event) => {
        const target = event.target as HTMLElement | null;
        if (!target || !target.classList.contains('js-post-type')) {
            return;
        }
        const tag = target.dataset.tag ?? '';
        const category = target.dataset.category ?? '';
        const labelText = (target.textContent ?? '').trim();
        if (labelText) {
            const headerText = header.querySelector('.post-type-dropdown-header-text');
            if (headerText) {
                headerText.textContent = labelText;
            }
        }
        menu.style.display = 'none';
        header.classList.remove('active-dropdown');
        await replaceList(tag, category);
    });
}

function initLoadMore(): void {
    document.addEventListener('click', async (event) => {
        const target = event.target as HTMLElement | null;
        if (!target || !target.classList.contains('js-load-more-posts')) {
            return;
        }
        event.preventDefault();

        const button = target as HTMLAnchorElement;
        const page = Number.parseInt(button.dataset.page ?? '1', 10);
        if (Number.isNaN(page)) {
            return;
        }

        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');

        try {
            const data = await fetchPage(page, currentTag, currentCategory);
            if (!data) {
                return;
            }
            const list = findList();
            if (list && data.items) {
                list.insertAdjacentHTML('beforeend', data.items);
            }
            setLoadMoreButton(data.nextPageIndex, data.hasMore);
        } finally {
            button.classList.remove('is-loading');
            button.removeAttribute('aria-busy');
        }
    });
}

function readInitialCategory(): string {
    // /press/news/ template emits a `data-category` attribute on the
    // initial Load More button so subsequent AJAX requests stay scoped
    // to the same category. /blog/ omits the attribute → empty string.
    const button = document.querySelector<HTMLAnchorElement>('a.js-load-more-posts[data-category]');
    return button?.dataset.category ?? '';
}

function init(): void {
    currentCategory = readInitialCategory();
    initCategoriesDropdown();
    initLoadMore();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

export {};
